import React, { useContext, useEffect, useId, useState } from 'react'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'
import { IconRegularRefresh } from '@notino/react-styleguide'

// types
import { PassDetail as PassDetailType } from '../../../../types/types'

// utils
import { DATE_FORMAT } from '../../../../utils/enums'
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

// components
import PassStatusBadge from '../PassStatusBadge/PassStatusBadge'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// assets
import MarketIcon from '../../../../assets/icons/MarketIcon'
import ScissorsIcon from '../../../../assets/icons/ScissorsIcon'
import CalendarIcon from '../../../../assets/icons/CalendarIcon'
import CalendarCheckIcon from '../../../../assets/icons/CalendarCheckIcon'
import TicketIcon from '../../../../assets/icons/TicketIcon'

// hooks
import useMessages from '../../../../hooks/useMessages'

// styles
import * as SC from './PassDetailStyles'

type PassDetailProps = {
	pass: PassDetailType
	passID: string
}

type PassQrCodeSectionProps = {
	passID: string
	passCode: string
}

const PassQrCodeSection = (props: PassQrCodeSectionProps) => {
	const { passID, passCode } = props
	const { apiBrowser } = useContext(AppContext)
	const { messages } = useMessages()
	const qrCodeHeadingId = useId()
	const [qrCodeSrc, setQrCodeSrc] = useState<string>()
	const [isLoading, setIsLoading] = useState(true)
	const [hasError, setHasError] = useState(false)
	const [reloadToken, setReloadToken] = useState(0)
	const [statusMessage, setStatusMessage] = useState('')

	useEffect(() => {
		let objectUrl: string | undefined
		let isCancelled = false

		const fetchQrCode = async () => {
			try {
				setIsLoading(true)
				setHasError(false)
				setStatusMessage(messages.Loading)
				// cache-busts the QR image URL so Reload cannot get a stale cached PNG.
				const qrCodeBlob = await apiBrowser.b2c.getPassQrCode(passID, { t: String(reloadToken) }, { displayNotification: false })

				if (isCancelled) {
					return
				}

				objectUrl = URL.createObjectURL(qrCodeBlob)
				setQrCodeSrc(objectUrl)
				setStatusMessage('')
			} catch {
				if (isCancelled) {
					return
				}

				setQrCodeSrc(undefined)
				setHasError(true)
				setStatusMessage('')
			} finally {
				if (!isCancelled) {
					setIsLoading(false)
				}
			}
		}

		fetchQrCode()

		return () => {
			isCancelled = true
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl)
			}
		}
	}, [apiBrowser, messages.Loading, passID, reloadToken])

	const renderQrCodeContent = () => {
		if (isLoading) {
			return (
				<SC.PassQrCodeImageWrapper aria-busy={true}>
					<SC.PassQrCodeSkeleton />
				</SC.PassQrCodeImageWrapper>
			)
		}

		if (hasError || !qrCodeSrc) {
			return (
				<SC.PassQrCodeError role='alert'>
					<SC.PassQrCodeErrorMessage>{messages['Could not load QR code. Please reload the screen']}</SC.PassQrCodeErrorMessage>
					<SC.PassQrCodeReloadButton type='button' onClick={() => setReloadToken((token) => token + 1)}>
						<IconRegularRefresh width='20px' height='20px' color='icon.primary' aria-hidden />
						<SC.PassQrCodeReloadLabel>{messages.Reload}</SC.PassQrCodeReloadLabel>
					</SC.PassQrCodeReloadButton>
				</SC.PassQrCodeError>
			)
		}

		return (
			<SC.PassQrCodeImageWrapper>
				{/* Decorative: pass code is already exposed as text below the image. */}
				<SC.PassQrCodeImage src={qrCodeSrc} alt='' onError={() => setHasError(true)} />
			</SC.PassQrCodeImageWrapper>
		)
	}

	return (
		<SC.PassQrCodeCard aria-labelledby={qrCodeHeadingId} aria-busy={isLoading || undefined}>
			<VisuallyHidden as='h2' id={qrCodeHeadingId}>
				{messages['Pass QR code']}
			</VisuallyHidden>
			<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
				{statusMessage}
			</VisuallyHidden>
			{renderQrCodeContent()}
			<SC.PassQrCodeValue>{passCode}</SC.PassQrCodeValue>
		</SC.PassQrCodeCard>
	)
}

const PassDetail = (props: PassDetailProps) => {
	const { pass, passID } = props
	const { locale } = useIntl()
	const { messages } = useMessages()
	const salonHref = pass.salon.seoSlugName ? PAGE_LINKS['/salons/:salonSlug'](locale, pass.salon.seoSlugName) : undefined

	return (
		<SC.PassDetailLayout>
			<SC.PassDetailMain>
				<SC.PassDetailTitle>{pass.service.name ?? messages['My pass']}</SC.PassDetailTitle>

				<SC.PassDetailSummary>
					<SC.PassDetailRowWithBadge>
						<SC.PassDetailLeftGroup>
							<SC.PassDetailIcon>
								<CalendarCheckIcon aria-hidden />
							</SC.PassDetailIcon>
							<SC.PassDetailLabel>{messages['Pass status']}</SC.PassDetailLabel>
						</SC.PassDetailLeftGroup>
						<PassStatusBadge passState={pass.state} />
					</SC.PassDetailRowWithBadge>

					<SC.PassDetailRow>
						<SC.PassDetailIcon>
							<MarketIcon aria-hidden />
						</SC.PassDetailIcon>
						<SC.PassDetailContent>
							<SC.PassDetailRowHeader>
								<SC.PassDetailLabel>{messages.Salon}</SC.PassDetailLabel>
								{salonHref && <SC.SeeDetailLink href={salonHref}>{messages['See detail']}</SC.SeeDetailLink>}
							</SC.PassDetailRowHeader>
							{pass.salon.name && <SC.PassDetailValue>{pass.salon.name}</SC.PassDetailValue>}
						</SC.PassDetailContent>
					</SC.PassDetailRow>

					<SC.PassDetailRow>
						<SC.PassDetailIcon>
							<ScissorsIcon aria-hidden />
						</SC.PassDetailIcon>
						<SC.PassDetailContent>
							<SC.PassDetailLabel>{messages.Service}</SC.PassDetailLabel>
							{pass.service.name && <SC.PassDetailValue>{pass.service.name}</SC.PassDetailValue>}
						</SC.PassDetailContent>
					</SC.PassDetailRow>

					<SC.PassDetailRow>
						<SC.PassDetailIcon>
							<TicketIcon aria-hidden />
						</SC.PassDetailIcon>
						<SC.PassDetailContent>
							<SC.PassDetailLabel>{messages['Entries used']}</SC.PassDetailLabel>
							<SC.PassDetailValue>
								{pass.usedEntries}/{pass.totalEntries}
							</SC.PassDetailValue>
						</SC.PassDetailContent>
					</SC.PassDetailRow>

					<SC.PassDetailRow>
						<SC.PassDetailIcon>
							<CalendarIcon aria-hidden />
						</SC.PassDetailIcon>
						<SC.PassDetailContent>
							<SC.PassDetailLabel>{messages['Expires on']}</SC.PassDetailLabel>
							<SC.PassDetailValue>{dayjs(pass.expiresAt).format(DATE_FORMAT)}</SC.PassDetailValue>
						</SC.PassDetailContent>
					</SC.PassDetailRow>
				</SC.PassDetailSummary>
			</SC.PassDetailMain>

			<SC.PassQrCodeSidebar>
				<PassQrCodeSection passID={passID} passCode={pass.code} />
			</SC.PassQrCodeSidebar>
		</SC.PassDetailLayout>
	)
}

export default PassDetail
